import type { ReactNode } from "react";
import { T } from "./tokens";

export function Tabs<K extends string>({ items, value, onChange, testid }: {
    items: { key: K; label: string }[]; value: K; onChange: (k: K) => void; testid: string;
}) {
    return (
        <div className="flex flex-wrap gap-1 rounded-xl p-1" style={{ background: T.page }} role="tablist" data-testid={testid}>
            {items.map((it) => {
                const on = it.key === value;
                return (
                    <button key={it.key} type="button" role="tab" aria-selected={on} onClick={() => onChange(it.key)}
                        data-testid={`${testid}-${it.key}`}
                        className="rounded-lg px-3 py-1.5 text-xs font-medium transition-[background-color,color,box-shadow] duration-200"
                        style={{ background: on ? T.card : "transparent", color: on ? T.primary : T.textMuted, boxShadow: on ? "0 1px 3px rgba(15,23,42,0.08)" : "none" }}>
                        {it.label}
                    </button>
                );
            })}
        </div>
    );
}

export function Stat({ label, value, tone = "default", testid }: { label: string; value: ReactNode; tone?: "default" | "success"; testid?: string }) {
    return (
        <div className="rounded-xl border p-3 transition-transform duration-200 hover:-translate-y-0.5" style={{ borderColor: T.border, background: T.card }} data-testid={testid}>
            <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: T.textMuted }}>{label}</div>
            <div className="text-lg font-semibold tabular-nums" style={{ color: tone === "success" ? T.success : T.text }}>{value}</div>
        </div>
    );
}

export function Avatar({ name }: { name: string }) {
    const initials = name.split(" ").map((p) => p[0]).join("").slice(0, 2);
    return (
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold" style={{ background: T.primarySoft, color: T.primary }}>{initials}</span>
    );
}

export function Bar({ pct, color = T.primary }: { pct: number; color?: string }) {
    return (
        <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: T.border }}>
            <div className="h-full rounded-full transition-[width] duration-500 ease-out" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
        </div>
    );
}

export const fmt = (n: number, lang: string) => new Intl.NumberFormat(lang).format(n);
